import { Project } from '../types';

/**
 * Technical project data with detailed descriptions
 * Focus on technical achievements and architecture decisions
 */

export const projects: Project[] = [
  {
    id: 'alfred',
    title: 'Alfred - Assistant de Cuisine Vocal',
    description: 'Assistant de cuisine d\'excellence technique avec intégration seamless des ingrédients et contrôle hands-free.',
    longDescription: `**Alfred - Votre Compagnon Culinaire Intelligent**

Fini les "qu'est-ce qu'on mange ce soir ?" et les courses oubliées ! Alfred transforme votre cuisine en un espace où cuisiner devient un plaisir simple et intuitif.

**Pourquoi Alfred :**
- **Terminé le gaspillage** : Alfred surveille vos aliments, vous alerte avant expiration et suggère des recettes pour utiliser ce que vous avez
- **Cuisinez sans stress** : Guidage vocal mains-libres, timers automatiques et conseils personnalisés selon votre niveau
- **Découvrez de nouveaux plats** : Recommandations intelligentes basées sur vos goûts, restrictions alimentaires et ce qui traîne dans votre frigo
- **Gagnez du temps** : Scannez vos courses, planifiez vos repas et laissez Alfred organiser votre semaine culinaire
- **Une cuisine connectée** : Contrôlez tout à la voix pendant que vous cuisinez, sans jamais toucher votre téléphone

**L'objectif :** Rendre la cuisine accessible à tous, réduire le stress des repas quotidiens et vous faire redécouvrir le plaisir de cuisiner, que vous soyez débutant ou chef confirmé.`,
    image: '/images/projects/alfred/alfred_home.png',
    technologies: [
      'Flutter', 
      'Dart', 
      'Clean Architecture', 
      'Material 3', 
      'Voice AI', 
      'Result<T> Pattern', 
      'Riverpod',
      'Freezed',
      'EventBus'
    ],
    githubUrl: 'https://github.com/adda-neb/alfred',
    liveUrl: 'https://alfred-cooking.vercel.app',
    featured: true,
    category: 'ai',
    status: 'in-progress',
    startDate: '2025-04',
    endDate: undefined,
  },
  {
    id: 'portfolio',
    title: 'Portfolio Swiss Knife',
    description: 'Portfolio modulaire avec outils intégrés et design system personnalisé.',
    longDescription: `Portfolio technique développé avec React, TypeScript et un design system personnalisé.

**Architecture & Stack :**
- **Frontend** : React 18 + TypeScript + Styled Components pour une architecture modulaire
- **Animations** : Framer Motion avec transitions fluides et effets LEGO sur les cards
- **Backend** : Node.js + Express pour les outils intégrés (Recipe Generator avec API Spoonacular)
- **Design System** : Thème personnalisé avec couleurs jaune/orange/rose et composants réutilisables
- **Internationalisation** : Support FR/EN avec contexte React
- **Protection** : Système anti-scraping optimisé avec détection de bots

**Outils Intégrés :**
- Recipe Generator avec autocomplétion d'ingrédients et API externe
- Red Thread (en développement) : moteur de recommandations de films
- Système de ressources curées avec filtrage avancé

**Performance :** Optimisé pour la production avec lazy loading, compression d'images, et SEO`,
    image: '/images/projects/portfolio/portfolio_home.png',
    technologies: [
      'React', 
      'TypeScript', 
      'Styled Components', 
      'Framer Motion', 
      'Node.js', 
      'Express',
      'Spoonacular API',
      'i18n'
    ],
    githubUrl: 'https://github.com/adda-neb/portfolio',
    liveUrl: 'https://adda-neb.vercel.app',
    featured: false,
    category: 'web',
    status: 'in-progress',
    startDate: '2025-09',
    endDate: undefined,
  },
  {
    id: 'systra-module',
    title: 'Module ERP SYSTRA',
    description: 'Module web intégré au système ERP propriétaire de SYSTRA pour la gestion des projets d\'infrastructure.',
    longDescription: `Module de gestion développé durant un stage chez SYSTRA, intégré au système ERP propriétaire de l'entreprise.

**Contexte Technique :**
- **Intégration ERP** : Développement d'un module web s'intégrant seamlessly dans l'écosystème SYSTRA existant
- **Architecture** : Clean Architecture avec séparation des couches Domain/Data/Presentation
- **Stack Technologique** : TypeScript, React, Node.js, PostgreSQL pour une stack moderne et maintenable
- **Gestion de Données** : Intégration avec les bases de données existantes et APIs internes SYSTRA
- **Interface Utilisateur** : Design cohérent avec l'identité visuelle SYSTRA et UX optimisée pour les équipes

**Défis Résolus :**
- Intégration avec les systèmes legacy existants
- Respect des contraintes de sécurité et de confidentialité
- Optimisation des performances pour de gros volumes de données
- Interface intuitive pour les utilisateurs métier

**Impact :** Amélioration de l'efficacité des équipes dans la gestion des projets d'infrastructure`,
    image: '/images/projects/systra/systra.webp',
    technologies: [
      'TypeScript', 
      'React', 
      'Node.js', 
      'PostgreSQL', 
      'ERP Integration', 
      'Clean Architecture',
      'Legacy Integration'
    ],
    githubUrl: undefined, // Confidentiel
    liveUrl: undefined, // Confidentiel
    featured: false,
    category: 'web',
    status: 'completed',
    startDate: '2024-01',
    endDate: '2024-03',
  },
];

/**
 * Get project by ID
 */
export const getProjectById = (id: string): Project | undefined => {
  return projects.find(project => project.id === id);
};

/**
 * Get featured project
 */
export const getFeaturedProject = (): Project | undefined => {
  return projects.find(project => project.featured);
};

/**
 * Get other projects (non-featured)
 */
export const getOtherProjects = (): Project[] => {
  return projects.filter(project => !project.featured);
};

/**
 * Get projects by category
 */
export const getProjectsByCategory = (category: string): Project[] => {
  return projects.filter(project => project.category === category);
};

/**
 * Get projects by status
 */
export const getProjectsByStatus = (status: string): Project[] => {
  return projects.filter(project => project.status === status);
};
